import DateService from 'services/dateService';
import _ from 'lodash';
import Config from 'config';
import { AppConstants } from 'constants/index';
import { AuthTokenResponse } from 'app/auth/types';
import axios from 'utils/axiosInterceptors';
import LocalStorageService from './localStorageService';
import { BaseQueryResult, NetworkBaseQueryArgs, NetworkRequestOptions } from './types';

const NetworkService = {
  makeAPIGetRequest: (partUrl: string | string[], options = {}) => NetworkService.makeAPIRequest(partUrl, {
    ...options,
    method: AppConstants.methods.GET,
  }),

  makeAPIPostRequest: (partUrl: string | string[], options = {}) => NetworkService.makeAPIRequest(partUrl, {
    ...options,
    method: AppConstants.methods.POST,
  }),

  makeAPIPutRequest: (partUrl: string | string[], options = {}) => NetworkService.makeAPIRequest(partUrl, {
    ...options,
    method: AppConstants.methods.PUT,
  }),

  makeAPIDeleteRequest: (partUrl: string | string[], options = {}) => NetworkService.makeAPIRequest(partUrl, {
    ...options,
    method: AppConstants.methods.DELETE,
  }),

  generateUrl: (arg: string | (string | number | boolean)[]) => (Array.isArray(arg) ? [...arg].join('/') : `/${arg}`),

  baseQuery: async <T>({
    url,
    method = AppConstants.methods.GET,
    data,
    params,
    meta,
    ...rest
  }: NetworkBaseQueryArgs): Promise<BaseQueryResult<T>> => {
    const { skipErrorHandler, includeErrorStatusCode, ...restMeta } = meta || {};
    try {
      const response = await NetworkService.makeAPIRequest(url, {
        method,
        data,
        params,
        ...rest,
      });

      return { data: response.data as T, meta: restMeta };
    } catch (err: unknown) {
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const { response } = (err as { response?: { status?: number; data?: unknown } });
        throw JSON.stringify({
          meta: { skipErrorHandler, includeErrorStatusCode },
          response: _.pick(response, ['status', 'data']),
        });
      }
      throw err;
    }
  },

  makeAPIRequest: (partUrl: string | (string | number | boolean)[], options: NetworkRequestOptions) => {
    const url = NetworkService.generateUrl(partUrl);
    const baseURL = options.baseURL || Config.baseURL;
    const auth = LocalStorageService.get('auth') as AuthTokenResponse;
    const authorization = auth && auth.access_token
      ? {
        Authorization: `Bearer ${auth.access_token}`,
      }
      : null;

    const headers: Record<string, string> = {
      ...Config.xhr.headers,
      ...authorization,
      ...options.headers,
      'Accept-Language': LocalStorageService.get<{ languageId: string }>('configs')?.languageId || AppConstants.defaultLanguage,
      Tz: DateService.tz,
    };

    const isFormData = options.data instanceof FormData;

    // If sending a file, let the browser handle the Content-Type
    if (isFormData) {
      delete headers['Content-Type'];
    }

    return axios({
      ...options,
      baseURL,
      url,
      headers,
    });
  },
};

export default NetworkService;
